import { hasActiveCapture } from "../buildings";
import { resetCaptureAt } from "../economy";
import { facingFromPath } from "../fixtures";
import { findUnit, getTile } from "../grid";
import type { GameEvent, GameState, Move } from "../types";
import { MAX_HP } from "../units";
import { mergeJoinedUnit, overflowGoldForJoin, pathDestination } from "./helpers";

type StepOutcome = { state: GameState; events: GameEvent[] };

export function runJoinMove(state: GameState, move: Extract<Move, { type: "join" }>): StepOutcome {
  const unit = findUnit(state, move.unitId);
  const target = findUnit(state, move.targetId);
  const dest = pathDestination(move.path, unit.pos);
  const facing = facingFromPath({
    path: move.path,
    startPos: unit.pos,
    fallback: unit.facing,
  });
  const sourceHp = Math.max(0, Math.min(unit.hp, MAX_HP));
  const targetHp = Math.max(0, Math.min(target.hp, MAX_HP));
  const combinedHp = sourceHp + targetHp;
  const finalHp = Math.min(MAX_HP, combinedHp);
  const overflowGold = overflowGoldForJoin(unit.kind, Math.max(0, combinedHp - MAX_HP));
  const hpGained = Math.max(0, finalHp - targetHp);
  const tileAtOrigin = getTile(state, unit.pos);
  const { state: base, events } = hasActiveCapture(tileAtOrigin)
    ? resetCaptureAt({ state, pos: unit.pos, reason: "moved" })
    : { state, events: [] as GameEvent[] };
  const merged = mergeJoinedUnit({ target, dest, facing, finalHp });
  return {
    state: {
      ...base,
      funds: {
        ...base.funds,
        [unit.owner]: base.funds[unit.owner] + overflowGold,
      },
      units: base.units
        .filter((u) => u.id !== unit.id)
        .map((u) => (u.id === target.id ? merged : u)),
    },
    events: [
      ...events,
      { type: "moved", unitId: unit.id, from: unit.pos, path: move.path },
      {
        type: "joined",
        unitId: unit.id,
        targetId: target.id,
        hpGained,
        overflowGold,
      },
    ],
  };
}
